import { Box, Stack, Typography } from "@mui/material";

import { dirTokens } from "../tokens";

/**
 * EmptyState — reusable "nothing here yet" placeholder.
 *
 * Replaces the `Typography inside colSpan TableCell` pattern every
 * list page copies. Also drops into detail-page section footers
 * (e.g. "Not a member of any account.") and picker "Nothing to
 * pick from." branches.
 *
 * Two flavors:
 *   - variant="empty"    — truly nothing exists yet (offers a CTA)
 *   - variant="no-match" — filter/search hides everything (no CTA;
 *                          shows how to clear the filter instead)
 *
 * Rendered with a dashed border so it reads as "expected placeholder",
 * not "broken screen".
 */
export default function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  variant = "empty",
  compact = false,
  sx,
}) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: compact ? 3 : 5,
        px: 3,
        borderRadius: dirTokens.radius,
        border: (theme) => `1px dashed ${theme.palette.divider}`,
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.01)",
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            color: "text.disabled",
            mb: 1.5,
            display: "inline-flex",
            fontSize: 40,
            "& svg": { fontSize: 40 },
          }}
        >
          {icon}
        </Box>
      )}
      <Stack spacing={0.5} alignItems="center" sx={{ maxWidth: 420 }}>
        {title && (
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Stack>
      {variant === "empty" && primaryAction && (
        <Box sx={{ mt: 2 }}>{primaryAction}</Box>
      )}
    </Box>
  );
}
